import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { config } from '../../config';
import Spinner from '../Global/Spinner';
import './scss/GitHubCallback.scss'; // Reuse existing callback styles

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing authentication...');

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const stateParam = searchParams.get('state');

        if (error) {
          throw new Error(`OAuth error: ${error}`);
        }

        if (!code) {
          throw new Error('No authorization code received');
        }

        if (!stateParam) {
          throw new Error('No state parameter received');
        }

        // Parse state data - handle both old JSON format and new simple format
        let stateData;
        try {
          // Try new simple format first: "state|base64Domain|provider|action"
          if (stateParam.includes('|')) {
            const parts = stateParam.split('|');
            if (parts.length === 4) {
              stateData = {
                state: parts[0],
                originalDomain: atob(parts[1]),
                provider: parts[2],
                action: parts[3]
              };
            } else {
              throw new Error('Invalid simple state format');
            }
          } else {
            // Fallback to old JSON format
            stateData = JSON.parse(decodeURIComponent(stateParam));
          }
        } catch (e) {
          throw new Error('Invalid state parameter format');
        }

        const { state, originalDomain, provider, action } = stateData;

        if (!state || !originalDomain || !provider || !action) {
          throw new Error('Missing required state data');
        }

        // Verify state matches stored data
        const storedStateData = localStorage.getItem('oauth_state_data');
        if (!storedStateData) {
          throw new Error('No stored state data found');
        }

        const storedData = JSON.parse(storedStateData);
        if (storedData.state !== state) {
          throw new Error('State mismatch - possible CSRF attack');
        }

        // Clean up stored data
        localStorage.removeItem('oauth_state_data');

        setMessage('Authenticating with server...');

        // Call the appropriate backend endpoint
        const endpoint = provider === 'github' ? '/v1/users/auth/github' : '/v1/users/auth/google';
        const authResponse = await fetch(config.API_URI + endpoint, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            code: code,
            action: action,
            originalDomain: originalDomain
          }),
        });

        if (!authResponse.ok) {
          const errorData = await authResponse.json();
          throw new Error(errorData.message || 'Authentication failed');
        }

        await authResponse.json();
        setStatus('success');
        setMessage('Authentication successful! Redirecting...');

        // Get user data
        const userResponse = await fetch(config.API_URI + '/v1/users/me', {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (userResponse.ok) {
          const userData = await userResponse.json();
          const username = userData.user.username;

          // Redirect back to the original domain
          setTimeout(() => {
            window.location.href = `${originalDomain}/u/${username}/boards`;
          }, 1000);
        } else {
          // Fallback redirect to original domain home
          setTimeout(() => {
            window.location.href = originalDomain;
          }, 1000);
        }

      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage(error.message || 'Authentication failed');

        // Try to get original domain from stored data for error redirect
        try {
          const storedStateData = localStorage.getItem('oauth_state_data');
          if (storedStateData) {
            const storedData = JSON.parse(storedStateData);
            setTimeout(() => {
              window.location.href = `${storedData.originalDomain}/login?error=${encodeURIComponent(error.message)}`;
            }, 2000);
            return;
          }
        } catch (e) {
          // Ignore parsing errors
        }

        // Fallback to main domain login
        setTimeout(() => {
          window.location.href = `${config.DOMAIN}/login?error=${encodeURIComponent(error.message)}`;
        }, 2000);
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate]);

  return (
    <div className="github-callback-container">
      <div className="github-callback-content">
        <div className="github-callback-spinner">
          <Spinner color='var(--brand-color)' size={40} strokeWidth={3} />
        </div>
        <h2>
          {status === 'processing' && 'Processing Authentication'}
          {status === 'success' && 'Authentication Successful'}
          {status === 'error' && 'Authentication Failed'}
        </h2>
        <p>{message}</p>
        {status === 'error' && (
          <p className="github-callback-error-note">
            You will be redirected back to the login page shortly.
          </p>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
